<template>
  <div v-if="visible">
    <button type="button" class="quick-chat-fab" title="Quick message about this page" @click="open">
      <i class="fe fe-message-square"></i>
      <span v-if="unread" class="quick-chat-badge">{{ unread > 99 ? "99+" : unread }}</span>
    </button>

    <ChatModal v-if="showing" title="Quick message" width="480px" @close="close">
      <div class="mb-3">
        <span class="chat-label d-block mb-1">About</span>
        <div v-if="loadingTag" class="quick-chat-tag text-muted">
          <span class="spinner-border spinner-border-sm mr-2"></span> Checking this page…
        </div>
        <div v-else-if="tag" class="quick-chat-tag">
          <i class="fe fe-paperclip mr-2 text-primary"></i>
          <div style="min-width: 0; flex: 1">
            <div class="font-weight-bold text-truncate">{{ tag.label }}</div>
            <small class="text-muted text-truncate d-block">{{ [typeLabel(tag.type), tag.sublabel].filter(Boolean).join(" · ") }}</small>
          </div>
          <button type="button" class="quick-chat-x" title="Don't attach this record" @click="tag = null">&times;</button>
        </div>
        <div v-else class="quick-chat-tag text-muted small">Nothing on this page to attach. The message is sent on its own.</div>
      </div>

      <label class="chat-label d-block mb-1" for="quickChatToInput">To</label>
      <RecipientPicker input-id="quickChatToInput" v-model="to" placeholder="Search people by name…" />

      <label class="chat-label d-block mb-1 mt-3" for="quickChatBody">Message</label>
      <textarea
        id="quickChatBody"
        ref="body"
        v-model="text"
        class="form-control"
        rows="4"
        :maxlength="bodyMax"
        placeholder="Type a message…"
        @keydown.enter.ctrl.prevent="send"
        @keydown.enter.meta.prevent="send"
      ></textarea>

      <div class="d-flex justify-content-end mt-4" style="gap: 8px">
        <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
        <button type="button" class="btn chat-btn px-4" :disabled="!canSend" @click="send">
          <span v-if="sending" class="spinner-border spinner-border-sm mr-1"></span> Send
        </button>
      </div>
    </ChatModal>
  </div>
</template>

<script>
import ChatModal from "./ChatModal.vue";
import RecipientPicker from "./RecipientPicker.vue";
import chat from "../store";
import api, { errorMessage } from "../api";
import { currentContext } from "../pageContext";

// Floating button on every page (mounted once in App.vue). Opens a small composer
// that attaches the record on screen (pageContext.js) so the user only picks
// recipients. One recipient: the message goes into the existing one-to-one chat.
export default {
  name: "QuickChat",
  components: { ChatModal, RecipientPicker },
  data() {
    return { showing: false, loadingTag: false, tag: null, to: [], text: "", sending: false, bodyMax: 4000 };
  },
  computed: {
    visible() {
      const path = this.$route.path;
      return chat.state.started && path !== "/" && !path.startsWith("/messages");
    },
    unread() {
      return chat.state.unread;
    },
    canSend() {
      return !this.sending && !this.loadingTag && this.to.length > 0 && (this.text.trim().length > 0 || !!this.tag);
    },
  },
  methods: {
    typeLabel(type) {
      const t = (chat.state.entityTypes || []).find((x) => x.key === type);
      return t ? t.label : "";
    },
    open() {
      Object.assign(this, { showing: true, tag: null, to: [], text: "", sending: false });
      // Bootstrap 4 modals (the pages' sidebars) pull focus back into themselves;
      // stop that so the composer can be typed in while a sidebar is open.
      if (window.jQuery) window.jQuery(document).off("focusin.bs.modal");
      this.loadTag(currentContext(this.$route));
    },
    close() {
      this.showing = false;
    },
    // Attach the record only if this user may tag its type and see it.
    loadTag(context) {
      if (!context) return;
      this.loadingTag = true;
      chat
        .loadEntityTypes()
        .then((types) => {
          if (!(types || []).some((t) => t.key === context.type)) return null;
          return api.entity(context.type, context.id);
        })
        .then((res) => {
          if (this.showing && res && res.can_open) {
            this.tag = { type: res.type, id: Number(res.id), label: res.label, sublabel: res.sublabel };
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loadingTag = false;
          this.$nextTick(() => this.$refs.body && this.$refs.body.focus());
        });
    },
    payload() {
      const tags = this.tag ? [{ type: this.tag.type, id: this.tag.id }] : [];
      const token = this.tag ? `[[${this.tag.type}:${this.tag.id}]]` : "";
      return { body: [this.text.trim(), token].filter(Boolean).join("\n"), tags };
    },
    send() {
      if (!this.canSend) return;
      this.sending = true;
      const { body, tags } = this.payload();
      const to = this.to.map((u) => u.id);

      const post =
        to.length === 1
          ? api
              .conversations({ with: to[0], per_page: 1 })
              .catch(() => ({ data: [] }))
              .then((res) =>
                res.data.length
                  ? api.reply(res.data[0].id, { body, tags }).then(() => res.data[0].id)
                  : api.createConversation({ to, cc: [], body, tags }).then((r) => r.conversation.id)
              )
          : api.createConversation({ to, cc: [], body, tags }).then((r) => r.conversation.id);

      post
        .then((conversationId) => {
          this.showing = false;
          this.$toasted.success("Message sent", {
            position: "top-center",
            duration: 4000,
            action: {
              text: "Open chat",
              onClick: (e, toast) => {
                toast.goAway(0);
                this.$router.push(`/messages/${conversationId}`).catch(() => {});
              },
            },
          });
        })
        .catch((error) => {
          this.$toasted.error(errorMessage(error, "Couldn't send the message."), { position: "top-center", duration: 4000 });
        })
        .finally(() => {
          this.sending = false;
        });
    },
  },
};
</script>

<style scoped>
.quick-chat-fab {
  position: fixed;
  right: 16px;
  bottom: 16px;
  /* above Bootstrap modals/sidebars (1050) so it works while one is open;
     below the chat toasts (1700) and the idle-timeout / pending-auth modals */
  z-index: 1690;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 0;
  background: #2563eb;
  color: #fff;
  font-size: 1.35rem;
  box-shadow: 0 8px 22px rgba(37, 99, 235, 0.4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.quick-chat-fab:hover {
  background: #1d4ed8;
}
.quick-chat-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: #dc2626;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 20px;
}
.quick-chat-tag {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}
.quick-chat-x {
  border: 0;
  background: transparent;
  color: #94a3b8;
  font-size: 1.3rem;
  line-height: 1;
  padding: 0 0 0 8px;
}
@media print {
  .quick-chat-fab {
    display: none;
  }
}
</style>
